import rawRiskMixMap from "./model-portfolio-mix-map-20260808T1200Z.json";
import type { InvestorAlphaPreference } from "../../types/investorAlpha";
import type { AlphaIbkrAccountType } from "./alphaIbkrAccountDetails";

export const ALPHA_RISK_TRACKS = [
  "Total Return",
  "Income",
  "Retirement Total Return",
  "Retirement Income",
] as const;
export const ALPHA_RISK_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export type AlphaRiskTrack = typeof ALPHA_RISK_TRACKS[number];
export type AlphaRiskKey = typeof ALPHA_RISK_KEYS[number];
type AlphaRiskKeyString = `${AlphaRiskKey}`;

export interface AlphaRiskMixMapEntry {
  readonly minimum_investment_amount_usd: number;
  readonly maximum_investment_amount_usd: number | null;
  readonly mix_id: string;
}

export type AlphaRiskMixMap = {
  readonly [Track in AlphaRiskTrack]: {
    readonly [Risk in AlphaRiskKeyString]: readonly AlphaRiskMixMapEntry[];
  };
};

export interface AlphaRiskScoreResolution {
  readonly riskKeys: readonly AlphaRiskKey[];
  readonly formattedScore: string;
}

export interface AlphaPlanMixSelection {
  readonly riskTrack: AlphaRiskTrack;
  readonly mixId: string;
}

const STANDARD_RISK_TRACK_BY_PREFERENCE: Record<
  InvestorAlphaPreference,
  "Total Return"
> = {
  growth: "Total Return",
  balance: "Total Return",
  protect: "Total Return",
};

const ENTRY_KEYS = [
  "minimum_investment_amount_usd",
  "maximum_investment_amount_usd",
  "mix_id",
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const isAlphaMixId = (value: unknown): value is string => {
  if (typeof value !== "string" || value.trim() === "" || value !== value.trim()) {
    return false;
  }

  const segments = value.startsWith("mix_") ? value.slice(4).split("_") : [];
  if (
    segments.length !== 6
    || segments.some((segment) => !/^(0|[1-9]\d*)$/.test(segment))
  ) {
    return false;
  }

  const weights = segments.map(Number);
  return weights.reduce((total, weight) => total + weight, 0) === 100;
};

const isValidUsdAmount = (value: unknown): value is number =>
  typeof value === "number"
  && Number.isSafeInteger(value)
  && value >= 0;

const hasExactKeys = (
  value: Record<string, unknown>,
  expectedKeys: readonly string[]
): boolean => {
  const actualKeys = Object.keys(value).sort();
  const sortedExpectedKeys = [...expectedKeys].sort();

  return actualKeys.length === sortedExpectedKeys.length
    && actualKeys.every((key, index) => key === sortedExpectedKeys[index]);
};

export function parseAlphaRiskMixMap(value: unknown): AlphaRiskMixMap {
  if (!isRecord(value) || !hasExactKeys(value, ALPHA_RISK_TRACKS)) {
    throw new Error(
      `Alpha risk map is malformed: expected ${ALPHA_RISK_TRACKS.join(", ")} tracks.`
    );
  }

  const riskKeyStrings = ALPHA_RISK_KEYS.map(String);
  for (const track of ALPHA_RISK_TRACKS) {
    const trackMap = value[track];
    if (!isRecord(trackMap) || !hasExactKeys(trackMap, riskKeyStrings)) {
      throw new Error(`Alpha risk map is malformed: ${track} must define risk keys 1 through 10.`);
    }

    for (const riskKey of riskKeyStrings) {
      const entries = trackMap[riskKey];
      if (!Array.isArray(entries) || entries.length === 0) {
        throw new Error(`Alpha risk map is malformed: ${track} risk ${riskKey} must contain mixes.`);
      }

      let previousMaximum: number | null | undefined;
      entries.forEach((entry, index) => {
        if (!isRecord(entry) || !hasExactKeys(entry, ENTRY_KEYS)) {
          throw new Error(
            `Alpha risk map is malformed: ${track} risk ${riskKey} contains an invalid entry.`
          );
        }

        const minimum = entry.minimum_investment_amount_usd;
        const maximum = entry.maximum_investment_amount_usd;
        if (
          !isValidUsdAmount(minimum)
          || (maximum !== null && !isValidUsdAmount(maximum))
          || (maximum !== null && maximum < minimum)
          || !isAlphaMixId(entry.mix_id)
        ) {
          throw new Error(
            `Alpha risk map is malformed: ${track} risk ${riskKey} contains invalid values.`
          );
        }

        if (index > 0 && (
          previousMaximum === null
          || previousMaximum === undefined
          || minimum !== previousMaximum + 1
        )) {
          throw new Error(
            `Alpha risk map is malformed: ${track} risk ${riskKey} has overlapping or missing amount bands.`
          );
        }

        if (maximum === null && index !== entries.length - 1) {
          throw new Error(
            `Alpha risk map is malformed: ${track} risk ${riskKey} has an open amount band before its final entry.`
          );
        }

        previousMaximum = maximum;
      });

      if (previousMaximum !== null) {
        throw new Error(
          `Alpha risk map is malformed: ${track} risk ${riskKey} must end with an open amount band.`
        );
      }
    }
  }

  return value as AlphaRiskMixMap;
}

const riskMixMap = parseAlphaRiskMixMap(rawRiskMixMap as unknown);

function assertRiskTrack(track: unknown): asserts track is AlphaRiskTrack {
  if (!ALPHA_RISK_TRACKS.some((candidate) => candidate === track)) {
    throw new Error(`Unsupported Alpha risk track: ${String(track)}.`);
  }
}

function assertRiskKey(riskKey: unknown): asserts riskKey is AlphaRiskKey {
  if (!ALPHA_RISK_KEYS.some((candidate) => candidate === riskKey)) {
    throw new Error(`Unsupported Alpha risk key: ${String(riskKey)}.`);
  }
}

function assertMixId(mixId: unknown): asserts mixId is string {
  if (!isAlphaMixId(mixId)) {
    throw new Error("Alpha mix ID is invalid.");
  }
}

function assertInvestmentAmount(investmentAmountUsd: unknown): asserts investmentAmountUsd is number {
  if (!isValidUsdAmount(investmentAmountUsd)) {
    throw new Error("Alpha investment amount must be a non-negative whole USD amount.");
  }
}

const includesInvestmentAmount = (
  entry: AlphaRiskMixMapEntry,
  investmentAmountUsd: number
): boolean =>
  investmentAmountUsd >= entry.minimum_investment_amount_usd
  && (
    entry.maximum_investment_amount_usd === null
    || investmentAmountUsd <= entry.maximum_investment_amount_usd
  );

export function resolveAlphaMixId(
  track: AlphaRiskTrack,
  riskKey: AlphaRiskKey,
  investmentAmountUsd: number
): string {
  assertRiskTrack(track);
  assertRiskKey(riskKey);
  assertInvestmentAmount(investmentAmountUsd);

  const entry = riskMixMap[track][String(riskKey) as AlphaRiskKeyString]
    .find((candidate) => includesInvestmentAmount(candidate, investmentAmountUsd));

  if (!entry) {
    throw new Error(
      `No Alpha mix mapping exists for ${track} risk ${riskKey} at ${investmentAmountUsd} USD.`
    );
  }

  return entry.mix_id;
}

export function resolveAlphaRiskTrack(
  preference: InvestorAlphaPreference,
  accountType: AlphaIbkrAccountType
): AlphaRiskTrack {
  const standardTrack = STANDARD_RISK_TRACK_BY_PREFERENCE[preference];
  if (!standardTrack) {
    throw new Error(`Unsupported Alpha investment preference: ${String(preference)}.`);
  }
  if (accountType !== "taxable" && accountType !== "retirement") {
    throw new Error(`Unsupported Alpha account type: ${String(accountType)}.`);
  }

  return accountType === "retirement" ? `Retirement ${standardTrack}` : standardTrack;
}

export function resolveAlphaPlanMixSelection(
  preference: InvestorAlphaPreference,
  accountType: AlphaIbkrAccountType,
  riskKey: AlphaRiskKey,
  investmentAmountUsd: number
): AlphaPlanMixSelection {
  const riskTrack = resolveAlphaRiskTrack(preference, accountType);

  return {
    riskTrack,
    mixId: resolveAlphaMixId(riskTrack, riskKey, investmentAmountUsd),
  };
}

// Smallest investable amount for a (track, risk key): the first amount band's
// minimum. parseAlphaRiskMixMap validates bands as ascending and contiguous, so
// the first entry always carries the lowest minimum. Below this amount no mix
// exists for the risk key at all.
export function getAlphaMixFloorUsd(
  track: AlphaRiskTrack,
  riskKey: AlphaRiskKey
): number {
  assertRiskTrack(track);
  assertRiskKey(riskKey);
  return riskMixMap[track][String(riskKey) as AlphaRiskKeyString][0]
    .minimum_investment_amount_usd;
}

export function getAlphaRiskKeys(
  track: AlphaRiskTrack,
  mixId: string,
  investmentAmountUsd?: number
): readonly AlphaRiskKey[] {
  assertRiskTrack(track);
  assertMixId(mixId);
  if (investmentAmountUsd !== undefined) {
    assertInvestmentAmount(investmentAmountUsd);
  }

  const matchingRiskKeys = ALPHA_RISK_KEYS.filter((riskKey) =>
    riskMixMap[track][String(riskKey) as AlphaRiskKeyString]
      .some((entry) =>
        entry.mix_id === mixId
        && (
          investmentAmountUsd === undefined
          || includesInvestmentAmount(entry, investmentAmountUsd)
        )
      )
  );

  if (matchingRiskKeys.length === 0) {
    const amountContext = investmentAmountUsd === undefined
      ? ""
      : ` at ${investmentAmountUsd} USD`;
    throw new Error(`No Alpha risk mapping exists for ${track} mix ${mixId}${amountContext}.`);
  }

  return matchingRiskKeys;
}

export function formatAlphaRiskScore(
  riskKeys: readonly AlphaRiskKey[]
): string {
  if (!Array.isArray(riskKeys) || riskKeys.length === 0) {
    throw new Error("Alpha risk keys are invalid.");
  }

  const sortedRiskKeys = [...riskKeys].sort((left, right) => left - right);
  if (
    sortedRiskKeys.some(
      (riskKey) => !Number.isInteger(riskKey) || riskKey < 1 || riskKey > 10
    )
    || new Set(sortedRiskKeys).size !== sortedRiskKeys.length
  ) {
    throw new Error("Alpha risk keys are invalid.");
  }

  if (sortedRiskKeys.length === 1) {
    return `${sortedRiskKeys[0]}/10`;
  }

  const isContiguous = sortedRiskKeys.every(
    (riskKey, index) => index === 0 || riskKey === sortedRiskKeys[index - 1] + 1
  );
  if (!isContiguous) {
    return sortedRiskKeys.map((riskKey) => `${riskKey}/10`).join(" or ");
  }

  return `${sortedRiskKeys[0]}\u2013${sortedRiskKeys[sortedRiskKeys.length - 1]}/10`;
}

export function resolveAlphaRiskScore(
  track: AlphaRiskTrack,
  mixId: string,
  investmentAmountUsd?: number
): AlphaRiskScoreResolution {
  const riskKeys = getAlphaRiskKeys(track, mixId, investmentAmountUsd);

  return {
    riskKeys,
    formattedScore: formatAlphaRiskScore(riskKeys),
  };
}
